import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Copy, Trash2, Pencil, Link2, Link2Off, BadgeCheck, RotateCw, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { cn } from '../lib/utils';
import { campaignStatusSummary, buildLaunchConfirmMessage, hasLaunchRecipients, canLaunchCampaign, launchCtaLabel, isExistingIssuedInvoiceForReminders, isTerminalInvoiceStatus } from '../lib/maintenanceCampaignUi';
import {
  applyBexioContactToForm,
  blankMaintenanceForm,
  buildMaintenanceItemPayload,
  formatLinkedBexioContact,
  formFromMaintenanceItem,
} from '../lib/maintenanceForm';
import { Button } from './ui/button';
import { BexioContactPicker } from './BexioContactPicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

export { campaignStatusSummary } from '../lib/maintenanceCampaignUi';

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];
const INVOICE_STATUSES = [
  { value: 'not_invoiced', label: 'Not invoiced' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'issued', label: 'Issued' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];
const DUE_STATUSES = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'soon_due', label: 'Soon due' },
  { value: 'due', label: 'Due' },
  { value: 'overdue', label: 'Overdue' },
];
const CURRENCIES = ['CHF', 'EUR', 'USD'];

function fmtDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function dueBadgeClass(status) {
  if (status === 'overdue') return 'bg-red-100 text-red-800 border-red-200';
  if (status === 'due') return 'bg-orange-100 text-orange-800 border-orange-200';
  if (status === 'soon_due') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
}

function websiteStatusDotClass(status) {
  if (status === 'online') return 'bg-emerald-500';
  if (status === 'offline') return 'bg-red-500';
  return 'bg-gray-300';
}

function pickLabel(options, value) {
  return options.find((o) => o.value === value)?.label || value;
}

function websiteHref(domain) {
  const raw = String(domain || '').trim();
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  return `https://${raw}`;
}

function linkedInvoiceCellValue(item) {
  return item.linked_invoice_number || item.linked_invoice_id || item.bexio_invoice_id || '-';
}

function maintenanceHasBexioInvoiceLink(item) {
  return !!(
    String(item.bexio_invoice_id || '').trim()
    || String(item.linked_invoice_id || '').trim()
    || String(item.linked_invoice_number || '').trim()
  );
}

function bexioRefCellValue(item, inlineEditId, inlineDraft) {
  const contactId = inlineEditId === item.id ? inlineDraft?.bexio_contact_id : item.bexio_contact_id;
  if (contactId == null || contactId === '') return '-';
  const name = String(item.customer_name || '').trim();
  return name ? `${name} · Contact #${contactId}` : `Contact #${contactId}`;
}

async function triggerMaintenanceInvoicePdfDownload(itemId, documentNr = '') {
  const res = await api.downloadMaintenanceInvoicePdf(itemId, { disposition: 'attachment' });
  const blob = res?.data;
  if (!blob) throw new Error('Invalid PDF response');
  const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
  const filenameRoot = String(documentNr || `invoice-${itemId}`)
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '') || `invoice-${itemId}`;
  const url = URL.createObjectURL(pdfBlob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${filenameRoot}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function itemToPayload(item, overrides = {}) {
  return {
    domain_name: item.domain_name,
    customer_name: item.customer_name,
    bexio_contact_id: item.bexio_contact_id ?? null,
    registration_date: item.registration_date,
    billing_frequency: item.billing_frequency,
    maintenance_fee_amount: Number(item.maintenance_fee_amount || 0),
    currency: item.currency || 'CHF',
    invoice_status: item.invoice_status || 'not_invoiced',
    bexio_invoice_id: item.bexio_invoice_id || null,
    notes: item.notes || '',
    hosting_provider: item.hosting_provider || null,
    internal_owner: item.internal_owner || null,
    responsible_email: item.responsible_email || null,
    drip_language: item.drip_language || 'en',
    is_active: item.is_active !== false,
    last_invoice_date: item.last_invoice_date || null,
    last_paid_date: item.last_paid_date || null,
    ...overrides,
  };
}

export function MaintenanceTab({ focusItemId = null }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => blankMaintenanceForm());
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineDraft, setInlineDraft] = useState(null);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [websiteStatusByDomain, setWebsiteStatusByDomain] = useState({});
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkTargetItem, setLinkTargetItem] = useState(null);
  const [linkInvoices, setLinkInvoices] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkFilter, setLinkFilter] = useState('');
  const [linkMessage, setLinkMessage] = useState('');
  const [linkingInvoiceId, setLinkingInvoiceId] = useState(null);
  const [launchingId, setLaunchingId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    billing_frequency: 'all',
    due_status: 'all',
    invoice_status: 'all',
    year: 'all',
    state: 'active',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        api.getMaintenanceItems(filters),
        api.getMaintenanceSummary(),
      ]);
      setItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
      setSummary(summaryRes.data || null);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Failed to load maintenance items');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!focusItemId) return;
    const t = window.setTimeout(() => {
      const sid = String(focusItemId);
      const sel =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? `[data-maint-row="${CSS.escape(sid)}"]`
          : `[data-maint-row="${sid.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;
      const el = document.querySelector(sel);
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 140);
    return () => window.clearTimeout(t);
  }, [focusItemId]);

  useEffect(() => {
    const domains = Array.from(new Set(items.map((i) => String(i.domain_name || '').trim()).filter(Boolean)));
    if (domains.length === 0) {
      setWebsiteStatusByDomain({});
      return;
    }
    let cancelled = false;
    api.getMaintenanceWebsiteStatus(domains)
      .then((res) => {
        if (cancelled) return;
        const out = {};
        for (const row of (res.data?.items || [])) {
          const key = String(row?.domain || '').trim().toLowerCase();
          if (!key) continue;
          out[key] = row?.status || 'unknown';
        }
        setWebsiteStatusByDomain(out);
      })
      .catch(() => {
        if (!cancelled) {
          setWebsiteStatusByDomain({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  const years = useMemo(() => {
    const set = new Set(items.map((i) => {
      const raw = String(i.next_fee_date || '');
      return raw.length >= 4 ? raw.slice(0, 4) : '';
    }).filter(Boolean));
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [items]);

  const totalFee = useMemo(
    () => items.reduce((acc, item) => acc + Number(item.maintenance_fee_amount || 0), 0),
    [items]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(blankMaintenanceForm());
    setDrawerOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm(formFromMaintenanceItem(item));
    setDrawerOpen(true);
  };

  const saveItem = async () => {
    const built = buildMaintenanceItemPayload(form);
    if (!built.ok) {
      toast.error(built.message || 'Domain, customer, and registration date are required.');
      return;
    }
    try {
      const payload = built.payload;
      if (editingId) {
        await api.updateMaintenanceItem(editingId, payload);
        toast.success('Maintenance item updated');
      } else {
        await api.createMaintenanceItem(payload);
        toast.success('Maintenance item created');
      }
      setDrawerOpen(false);
      await loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Save failed'));
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete maintenance item?')) return;
    try {
      await api.deleteMaintenanceItem(id);
      toast.success('Maintenance item deleted');
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const duplicateItem = async (item) => {
    try {
      const payload = {
        ...item,
        domain_name: `${item.domain_name}-copy`,
        bexio_invoice_id: item.bexio_invoice_id || null,
        notes: item.notes || '',
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.next_fee_date;
      delete payload.due_status;
      await api.createMaintenanceItem(payload);
      toast.success('Maintenance item duplicated');
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  const openLinkBexioDialog = async (item) => {
    setLinkTargetItem(item);
    setLinkDialogOpen(true);
    setLinkLoading(true);
    setLinkInvoices([]);
    setLinkFilter('');
    setLinkMessage('');
    try {
      const res = await api.getBexioMaintenanceInvoices(item.id);
      const payload = res.data || {};
      if (payload.match_state !== 'ok') {
        setLinkMessage(payload.message || 'Could not resolve Bexio contact for this maintenance item');
        return;
      }
      const invoices = Array.isArray(payload.invoices) ? payload.invoices : [];
      setLinkInvoices(invoices);
      if (invoices.length === 0) {
        setLinkMessage('No Bexio invoices found for this maintenance contact');
      }
    } catch (e) {
      setLinkMessage(e?.response?.data?.detail || 'Failed to load Bexio invoices');
    } finally {
      setLinkLoading(false);
    }
  };

  const submitLinkBexioInvoice = async (invoiceId) => {
    if (!linkTargetItem?.id) return;
    setLinkingInvoiceId(invoiceId);
    try {
      await api.linkBexioMaintenanceInvoice(linkTargetItem.id, invoiceId);
      toast.success('Bexio invoice linked to maintenance item');
      setLinkDialogOpen(false);
      setLinkTargetItem(null);
      setLinkInvoices([]);
      setLinkFilter('');
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link Bexio invoice');
    } finally {
      setLinkingInvoiceId(null);
    }
  };

  const unlinkBexioInvoice = async (item) => {
    if (
      !window.confirm(
        'Remove the Bexio invoice link from this maintenance item? Reconciliation will stop counting this row against that invoice until you link again.',
      )
    ) {
      return;
    }
    try {
      await api.unlinkMaintenanceBexioInvoice(item.id);
      toast.success('Bexio invoice unlinked');
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unlink Bexio invoice');
    }
  };

  const quickInvoiceStatus = async (item, invoice_status) => {
    try {
      const payload = itemToPayload(item, { invoice_status });
      await api.updateMaintenanceItem(item.id, payload);
      if (invoice_status === 'paid') {
        toast.success('AR settled and maintenance cycle renewed');
      }
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update invoice status');
    }
  };

  const markMaintenancePaid = async (item) => {
    const outstanding = Number(item.ar_balance_chf || 0);
    if (outstanding <= 0.005) {
      toast.error('This maintenance item has no outstanding balance');
      return;
    }
    if (!window.confirm(`Post payment of ${outstanding.toFixed(2)} CHF to clear this maintenance AR balance?`)) return;
    try {
      await api.addMaintenancePayment(item.id, {
        amount_chf: Number(outstanding.toFixed(2)),
        payment_date: new Date().toISOString().slice(0, 10),
        note: `Maintenance paid action · ${item.domain_name}`,
        source_amount: Number(outstanding.toFixed(2)),
        source_currency: 'CHF',
      });
      toast.success('Payment logged and AR updated');
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post payment');
    }
  };

  const renewMaintenanceCycle = async (item) => {
    if (!window.confirm(`Renew current cycle for ${item.domain_name}? This moves Next Fee Date to Last Fee Date and starts a new cycle.`)) return;
    try {
      await api.renewMaintenanceItem(item.id, {});
      toast.success('Maintenance cycle renewed');
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to renew maintenance cycle');
    }
  };

  const startInlineEdit = (item) => {
    setInlineEditId(item.id);
    setInlineDraft(formFromMaintenanceItem(item));
  };

  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineDraft(null);
    setInlineSaving(false);
  };

  const saveInlineEdit = async (item) => {
    if (!inlineDraft) return;
    const built = buildMaintenanceItemPayload({
      ...formFromMaintenanceItem(item),
      ...inlineDraft,
    });
    if (!built.ok) {
      toast.error(built.message || 'Domain, customer, and registration date are required.');
      return;
    }
    setInlineSaving(true);
    try {
      await api.updateMaintenanceItem(item.id, built.payload);
      toast.success('Maintenance item updated');
      cancelInlineEdit();
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Inline save failed');
    } finally {
      setInlineSaving(false);
    }
  };

  const launchCampaign = async (item) => {
    if (!canLaunchCampaign(item)) {
      toast.error(
        isTerminalInvoiceStatus(item)
          ? 'Paid/cancelled lines cannot Launch reminders.'
          : 'Campaign already prepared for this cycle.',
      );
      return;
    }
    if (!hasLaunchRecipients(item)) {
      toast.error(
        'Add Reminder recipient email(s) on this maintenance line before Launch. Launch never uses a global default.',
      );
      return;
    }
    if (!window.confirm(buildLaunchConfirmMessage(item))) return;
    setLaunchingId(item.id);
    try {
      const res = await api.launchMaintenanceCampaign(item.id);
      const data = res?.data || {};
      if (data.ok) {
        toast.success(
          data.already_launched
            ? (data.message || 'Campaign already launched')
            : (data.message || 'Invoice launched & campaign enrolled'),
        );
        if (data.pdf_download_path) {
          try {
            await triggerMaintenanceInvoicePdfDownload(item.id, data.document_nr || '');
          } catch (pdfErr) {
            console.error(pdfErr);
            toast.error(
              data.email_sent
                ? 'Invoice emailed but PDF download failed — open the linked invoice to download.'
                : 'Invoice prepared but PDF download failed — open the linked invoice to download.',
            );
          }
        }
      } else {
        toast.error(data.message || data.error || 'Launch incomplete — check status and retry');
        if (data.pdf_download_path && data.invoice_id) {
          try {
            await triggerMaintenanceInvoicePdfDownload(item.id, data.document_nr || '');
          } catch {
            /* partial recovery UI will show */
          }
        }
      }
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Launch failed');
    } finally {
      setLaunchingId(null);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50/50 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <div className="rounded-md border bg-white px-3 py-2 text-xs"><div className="text-gray-500">Active hosted sites</div><div className="font-semibold">{summary?.active_hosted_sites ?? 0}</div></div>
            <div className="rounded-md border bg-white px-3 py-2 text-xs"><div className="text-gray-500">MRR potential</div><div className="font-semibold">{Number(summary?.monthly_recurring_potential ?? 0).toFixed(2)} CHF</div></div>
            <div className="rounded-md border bg-white px-3 py-2 text-xs"><div className="text-gray-500">ARR potential</div><div className="font-semibold">{Number(summary?.annual_recurring_potential ?? 0).toFixed(2)} CHF</div></div>
            <div className="rounded-md border bg-white px-3 py-2 text-xs"><div className="text-gray-500">Due soon</div><div className="font-semibold">{summary?.due_soon ?? 0}</div></div>
            <div className="rounded-md border bg-white px-3 py-2 text-xs"><div className="text-gray-500">Overdue</div><div className="font-semibold">{summary?.overdue ?? 0}</div></div>
            <div className="rounded-md border bg-white px-3 py-2 text-xs"><div className="text-gray-500">Paid this year</div><div className="font-semibold">{summary?.paid_this_year ?? 0}</div></div>
          </div>
          <Button onClick={openCreate} className="gap-1.5">
            <Plus className="h-4 w-4" />
            New Maintenance Item
          </Button>
        </div>
      </div>

      <div className="border-b border-gray-200 bg-white px-4 py-2">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-6">
          <input className="rounded border px-2 py-1 text-xs" placeholder="Search domain/customer" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
          <select className="rounded border px-2 py-1 text-xs" value={filters.billing_frequency} onChange={(e) => setFilters((f) => ({ ...f, billing_frequency: e.target.value }))}><option value="all">All frequencies</option>{FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}</select>
          <select className="rounded border px-2 py-1 text-xs" value={filters.due_status} onChange={(e) => setFilters((f) => ({ ...f, due_status: e.target.value }))}><option value="all">All due statuses</option>{DUE_STATUSES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}</select>
          <select className="rounded border px-2 py-1 text-xs" value={filters.invoice_status} onChange={(e) => setFilters((f) => ({ ...f, invoice_status: e.target.value }))}><option value="all">All invoice statuses</option>{INVOICE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select>
          <select className="rounded border px-2 py-1 text-xs" value={filters.year} onChange={(e) => setFilters((f) => ({ ...f, year: e.target.value }))}><option value="all">All years</option>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
          <select className="rounded border px-2 py-1 text-xs" value={filters.state} onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))}><option value="active">Active</option><option value="archived">Archived</option><option value="all">All</option></select>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-3 sm:p-4">
        <div className="space-y-3 md:hidden">
          {items.map((item) => (
            <div
              key={item.id}
              data-maint-row={item.id}
              className={cn(
                'rounded-lg border bg-white p-3 shadow-sm',
                focusItemId && focusItemId === item.id && 'ring-2 ring-sky-400/80',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${websiteStatusDotClass(websiteStatusByDomain[String(item.domain_name || '').trim().toLowerCase()] || 'unknown')}`}
                      title={`Website status: ${websiteStatusByDomain[String(item.domain_name || '').trim().toLowerCase()] || 'unknown'}`}
                    />
                    <a
                      href={websiteHref(item.domain_name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-sm font-semibold text-blue-700 hover:underline"
                    >
                      {item.domain_name}
                    </a>
                  </div>
                  <div className="mt-1 truncate text-xs text-gray-600">{item.customer_name}</div>
                  <div className="mt-1 truncate text-[11px] text-gray-500">
                    Recipients: {item.responsible_email || '— (global default)'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums">
                    {Number(item.maintenance_fee_amount || 0).toFixed(2)} {item.currency || 'CHF'}
                  </div>
                  <div className="mt-1 text-[11px] text-gray-500">{pickLabel(FREQUENCIES, item.billing_frequency)}</div>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded border bg-gray-50 px-2 py-1.5">
                  <div className="text-gray-500">Next Fee Date</div>
                  <div className="font-medium text-gray-900">{fmtDate(item.next_fee_date)}</div>
                </div>
                <div className="rounded border bg-gray-50 px-2 py-1.5">
                  <div className="text-gray-500">Last Fee Date</div>
                  <div className="font-medium text-gray-900">{fmtDate(item.last_fee_date)}</div>
                </div>
                <div className="col-span-2 flex items-center justify-between rounded border bg-gray-50 px-2 py-1.5">
                  <span className="text-gray-500">Due Status</span>
                  <span className={`inline-flex rounded border px-2 py-0.5 text-[11px] ${dueBadgeClass(item.due_status)}`}>
                    {pickLabel(DUE_STATUSES, item.due_status)}
                  </span>
                </div>
                <div className="col-span-2 rounded border bg-gray-50 px-2 py-1.5">
                  <div className="text-gray-500">Linked Invoice</div>
                  <div className="font-medium text-gray-900">{linkedInvoiceCellValue(item)}</div>
                </div>
                <div className="col-span-2 rounded border bg-gray-50 px-2 py-1.5">
                  <div className="text-gray-500">Campaign</div>
                  <div className="font-medium text-gray-900">{campaignStatusSummary(item).launchedLabel}</div>
                  <div className="mt-0.5 text-gray-600">Next: {campaignStatusSummary(item).nextLabel}</div>
                  {item.campaign_launch_error ? (
                    <div className="mt-0.5 text-amber-700">{item.campaign_launch_error}</div>
                  ) : null}
                </div>
                <div className="col-span-2 rounded border bg-gray-50 px-2 py-1.5">
                  <div className="text-gray-500">Bexio Ref</div>
                  <div className="font-medium text-gray-900">{bexioRefCellValue(item, inlineEditId, inlineDraft)}</div>
                </div>
              </div>

              <div className="mt-2">
                <Button
                  className="h-8 w-full gap-1.5 text-xs"
                  onClick={() => launchCampaign(item)}
                  disabled={launchingId === item.id || !canLaunchCampaign(item)}
                  title={
                    canLaunchCampaign(item)
                      ? launchCtaLabel(item)
                      : (campaignStatusSummary(item).stopped
                        ? 'Paid/cancelled — reminders stopped'
                        : 'Campaign already prepared')
                  }
                >
                  <Rocket className="h-3.5 w-3.5" />
                  {launchCtaLabel(item, { launching: launchingId === item.id })}
                </Button>
              </div>

              <div className="mt-2 flex items-center gap-1.5">
                <select
                  className="h-8 min-w-0 flex-1 rounded border px-2 py-1 text-xs"
                  value={inlineEditId === item.id ? (inlineDraft?.invoice_status || 'not_invoiced') : item.invoice_status}
                  onChange={(e) => {
                    if (inlineEditId === item.id) {
                      setInlineDraft((d) => ({ ...d, invoice_status: e.target.value }));
                    } else {
                      quickInvoiceStatus(item, e.target.value);
                    }
                  }}
                >
                  {INVOICE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  title="Renew cycle"
                  onClick={() => renewMaintenanceCycle(item)}
                >
                  <RotateCw className="h-3.5 w-3.5" />
                </Button>
              </div>

              {inlineEditId === item.id ? (
                <div className="mt-2 space-y-2 rounded border bg-gray-50 p-2">
                  <input
                    className="w-full rounded border px-2 py-1 text-xs"
                    value={inlineDraft?.domain_name || ''}
                    onChange={(e) => setInlineDraft((d) => ({ ...d, domain_name: e.target.value }))}
                    placeholder="Domain"
                  />
                  <div className="flex items-center gap-1">
                    <input
                      className="w-full rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.customer_name || ''}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, customer_name: e.target.value, bexio_contact_id: null }))}
                      placeholder="Customer"
                    />
                    <BexioContactPicker
                      onPick={(contact) => setInlineDraft((d) => ({
                        ...applyBexioContactToForm(d, contact),
                      }))}
                    />
                  </div>
                  <input
                    className="w-full rounded border px-2 py-1 text-xs"
                    value={inlineDraft?.responsible_email || ''}
                    onChange={(e) => setInlineDraft((d) => ({ ...d, responsible_email: e.target.value }))}
                    placeholder="Reminder recipient email(s)"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className="rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.billing_frequency || 'monthly'}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, billing_frequency: e.target.value }))}
                    >
                      {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                    <input
                      type="date"
                      className="rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.registration_date || ''}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, registration_date: e.target.value }))}
                    />
                    <input
                      type="number"
                      step="0.01"
                      className="rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.maintenance_fee_amount || '0'}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, maintenance_fee_amount: e.target.value }))}
                    />
                    <select
                      className="rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.currency || 'CHF'}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, currency: e.target.value }))}
                    >
                      {CURRENCIES.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <Button variant="outline" className="h-8 text-xs" onClick={() => cancelInlineEdit()} disabled={inlineSaving}>Cancel</Button>
                    <Button className="h-8 text-xs" onClick={() => saveInlineEdit(item)} disabled={inlineSaving}>
                      {inlineSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outline" className="h-8 text-xs" onClick={() => openEdit(item)}>Full</Button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-6">
                  <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => startInlineEdit(item)} title="Inline edit"><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => openLinkBexioDialog(item)} title="Link Bexio invoice"><Link2 className="h-3.5 w-3.5" /></Button>
                  {maintenanceHasBexioInvoiceLink(item) ? (
                    <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => unlinkBexioInvoice(item)} title="Unlink Bexio invoice"><Link2Off className="h-3.5 w-3.5" /></Button>
                  ) : null}
                  <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => markMaintenancePaid(item)} title="Mark paid"><BadgeCheck className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => duplicateItem(item)} title="Duplicate"><Copy className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => removeItem(item.id)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
          ))}
          {!loading && items.length === 0 && (
            <div className="rounded-lg border bg-white px-3 py-8 text-center text-sm text-gray-400">
              No maintenance items found.
            </div>
          )}
          {items.length > 0 && (
            <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-700">Total Fee</span>
                <span className="font-semibold tabular-nums text-gray-900">{totalFee.toFixed(2)} CHF</span>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block">
          <table className="w-full min-w-[1200px] border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="text-xs text-gray-500">
              <th className="border-b px-2 py-2 text-left">Domain</th>
              <th className="border-b px-2 py-2 text-left">Customer</th>
              <th className="border-b px-2 py-2 text-left">Reminder recipient email(s)</th>
              <th className="border-b px-2 py-2 text-left">Frequency</th>
              <th className="border-b px-2 py-2 text-right">Fee</th>
              <th className="border-b px-2 py-2 text-center">Registration Date</th>
              <th className="border-b px-2 py-2 text-center">Last Fee Date</th>
              <th className="border-b px-2 py-2 text-center">Next Fee Date</th>
              <th className="border-b px-2 py-2 text-center">Due Status</th>
              <th className="border-b px-2 py-2 text-center">Invoice Status</th>
              <th className="border-b px-2 py-2 text-left">Linked Invoice</th>
              <th className="border-b px-2 py-2 text-left">Campaign</th>
              <th className="border-b px-2 py-2 text-left">Bexio Ref</th>
              <th className="border-b px-2 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                data-maint-row={item.id}
                className={cn(
                  'hover:bg-gray-50',
                  focusItemId && focusItemId === item.id && 'bg-sky-50/90 ring-2 ring-inset ring-sky-400/80',
                )}
              >
                <td className="border-b px-2 py-2">
                  {inlineEditId === item.id ? (
                    <input
                      className="w-full rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.domain_name || ''}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, domain_name: e.target.value }))}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2.5 w-2.5 rounded-full ${websiteStatusDotClass(websiteStatusByDomain[String(item.domain_name || '').trim().toLowerCase()] || 'unknown')}`}
                        title={`Website status: ${websiteStatusByDomain[String(item.domain_name || '').trim().toLowerCase()] || 'unknown'}`}
                      />
                      <a
                        href={websiteHref(item.domain_name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {item.domain_name}
                      </a>
                    </div>
                  )}
                </td>
                <td className="border-b px-2 py-2">
                  {inlineEditId === item.id ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <input
                          className="w-full rounded border px-2 py-1 text-xs"
                          value={inlineDraft?.customer_name || ''}
                          onChange={(e) => setInlineDraft((d) => ({ ...d, customer_name: e.target.value, bexio_contact_id: null }))}
                        />
                        <BexioContactPicker
                          onPick={(contact) => setInlineDraft((d) => applyBexioContactToForm(d, contact))}
                        />
                      </div>
                      {formatLinkedBexioContact(inlineDraft) ? (
                        <div className="text-[11px] text-emerald-700">{formatLinkedBexioContact(inlineDraft)}</div>
                      ) : null}
                    </div>
                  ) : item.customer_name}
                </td>
                <td className="border-b px-2 py-2">
                  {inlineEditId === item.id ? (
                    <input
                      className="w-full min-w-[10rem] rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.responsible_email || ''}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, responsible_email: e.target.value }))}
                      placeholder="ops@…, finance@…"
                    />
                  ) : (
                    <span className="text-xs text-gray-700">{item.responsible_email || '—'}</span>
                  )}
                </td>
                <td className="border-b px-2 py-2">
                  {inlineEditId === item.id ? (
                    <select
                      className="w-full rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.billing_frequency || 'monthly'}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, billing_frequency: e.target.value }))}
                    >
                      {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  ) : pickLabel(FREQUENCIES, item.billing_frequency)}
                </td>
                <td className="border-b px-2 py-2 text-right tabular-nums">
                  {inlineEditId === item.id ? (
                    <div className="flex items-center justify-end gap-1">
                      <input
                        type="number"
                        step="0.01"
                        className="w-24 rounded border px-2 py-1 text-xs text-right"
                        value={inlineDraft?.maintenance_fee_amount || '0'}
                        onChange={(e) => setInlineDraft((d) => ({ ...d, maintenance_fee_amount: e.target.value }))}
                      />
                      <select
                        className="rounded border px-1 py-1 text-xs"
                        value={inlineDraft?.currency || 'CHF'}
                        onChange={(e) => setInlineDraft((d) => ({ ...d, currency: e.target.value }))}
                      >
                        {CURRENCIES.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
                      </select>
                    </div>
                  ) : <>{Number(item.maintenance_fee_amount || 0).toFixed(2)} {item.currency || 'CHF'}</>}
                </td>
                <td className="border-b px-2 py-2 text-center">
                  {inlineEditId === item.id ? (
                    <input
                      type="date"
                      className="rounded border px-2 py-1 text-xs"
                      value={inlineDraft?.registration_date || ''}
                      onChange={(e) => setInlineDraft((d) => ({ ...d, registration_date: e.target.value }))}
                    />
                  ) : fmtDate(item.registration_date)}
                </td>
                <td className="border-b px-2 py-2 text-center">{fmtDate(item.last_fee_date)}</td>
                <td className="border-b px-2 py-2 text-center">{fmtDate(item.next_fee_date)}</td>
                <td className="border-b px-2 py-2 text-center">
                  <span className={`inline-flex rounded border px-2 py-0.5 text-[11px] ${dueBadgeClass(item.due_status)}`}>
                    {pickLabel(DUE_STATUSES, item.due_status)}
                  </span>
                </td>
                <td className="border-b px-2 py-2 text-center">
                  <div className="inline-flex items-center gap-1">
                    <select
                      className="rounded border px-1 py-0.5 text-xs"
                      value={inlineEditId === item.id ? (inlineDraft?.invoice_status || 'not_invoiced') : item.invoice_status}
                      onChange={(e) => {
                        if (inlineEditId === item.id) {
                          setInlineDraft((d) => ({ ...d, invoice_status: e.target.value }));
                        } else {
                          quickInvoiceStatus(item, e.target.value);
                        }
                      }}
                    >
                      {INVOICE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      title="Renew cycle"
                      onClick={() => renewMaintenanceCycle(item)}
                    >
                      <RotateCw className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
                <td className="border-b px-2 py-2">{linkedInvoiceCellValue(item)}</td>
                <td className="border-b px-2 py-2 text-xs">
                  <div className="font-medium text-gray-900">{campaignStatusSummary(item).launchedLabel}</div>
                  <div className="text-gray-600">Next: {campaignStatusSummary(item).nextLabel}</div>
                  {item.campaign_launch_error ? (
                    <div className="text-amber-700">{item.campaign_launch_error}</div>
                  ) : null}
                </td>
                <td className="border-b px-2 py-2">
                  {bexioRefCellValue(item, inlineEditId, inlineDraft)}
                </td>
                <td className="border-b px-2 py-2">
                  <div className="flex justify-end gap-1">
                    {inlineEditId === item.id ? (
                      <>
                        <Button variant="outline" className="h-7 px-2 text-xs" onClick={() => cancelInlineEdit()} disabled={inlineSaving}>Cancel</Button>
                        <Button className="h-7 px-2 text-xs" onClick={() => saveInlineEdit(item)} disabled={inlineSaving}>
                          {inlineSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} title="Open full editor"><Pencil className="h-3.5 w-3.5" /></Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="secondary"
                          className="h-7 gap-1 px-2 text-xs"
                          onClick={() => launchCampaign(item)}
                          disabled={launchingId === item.id || !canLaunchCampaign(item)}
                          title={
                            canLaunchCampaign(item)
                              ? launchCtaLabel(item)
                              : (campaignStatusSummary(item).stopped
                                ? 'Paid/cancelled — reminders stopped'
                                : 'Campaign already prepared')
                          }
                        >
                          <Rocket className="h-3.5 w-3.5" />
                          {launchCtaLabel(item, { launching: launchingId === item.id, compact: true })}
                        </Button>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => startInlineEdit(item)} title="Inline edit"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => openLinkBexioDialog(item)} title="Link Bexio invoice"><Link2 className="h-3.5 w-3.5" /></Button>
                        {maintenanceHasBexioInvoiceLink(item) ? (
                          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => unlinkBexioInvoice(item)} title="Unlink Bexio invoice"><Link2Off className="h-3.5 w-3.5" /></Button>
                        ) : null}
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => markMaintenancePaid(item)} title="Mark paid"><BadgeCheck className="h-3.5 w-3.5" /></Button>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => duplicateItem(item)}><Copy className="h-3.5 w-3.5" /></Button>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => removeItem(item.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td className="px-2 py-8 text-center text-sm text-gray-400" colSpan={14}>No maintenance items found.</td></tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50/70 text-sm">
                <td className="border-t px-2 py-2 font-semibold" colSpan={4}>Total</td>
                <td className="border-t px-2 py-2 text-right font-semibold tabular-nums">{totalFee.toFixed(2)} CHF</td>
                <td className="border-t px-2 py-2" colSpan={9} />
              </tr>
            </tfoot>
          )}
          </table>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/20">
          <div className="h-full w-full max-w-xl overflow-auto bg-white p-4 shadow-xl">
            <h3 className="mb-3 text-sm font-semibold">{editingId ? 'Edit Maintenance Item' : 'New Maintenance Item'}</h3>
            {editingId ? (
              <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-gray-700">
                <div><span className="text-gray-500">Campaign:</span> {campaignStatusSummary(items.find((i) => i.id === editingId) || form).launchedLabel}</div>
                <div className="mt-0.5"><span className="text-gray-500">Next step:</span> {campaignStatusSummary(items.find((i) => i.id === editingId) || {}).nextLabel}</div>
                <div className="mt-0.5"><span className="text-gray-500">Linked invoice:</span> {linkedInvoiceCellValue(items.find((i) => i.id === editingId) || {})}</div>
                {(items.find((i) => i.id === editingId) || {}).campaign_launch_error ? (
                  <div className="mt-0.5 text-amber-700">{(items.find((i) => i.id === editingId) || {}).campaign_launch_error}</div>
                ) : null}
                <div className="mt-2">
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={() => {
                      const row = items.find((i) => i.id === editingId);
                      if (row) launchCampaign(row);
                    }}
                    disabled={
                      launchingId === editingId
                      || !canLaunchCampaign(items.find((i) => i.id === editingId) || {})
                    }
                  >
                    <Rocket className="h-3.5 w-3.5" />
                    {launchCtaLabel(items.find((i) => i.id === editingId) || {}, {
                      launching: launchingId === editingId,
                    })}
                  </Button>
                </div>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-xs text-gray-600" htmlFor="maint-domain">Domain name
                <input
                  id="maint-domain"
                  data-testid="maint-domain"
                  className="mt-1 w-full rounded border px-2 py-1 text-sm"
                  value={form.domain_name}
                  onChange={(e) => setForm((f) => ({ ...f, domain_name: e.target.value }))}
                />
              </label>
              <label className="text-xs text-gray-600" htmlFor="maint-registration-date">Registration date
                <input
                  id="maint-registration-date"
                  data-testid="maint-registration-date"
                  type="date"
                  className="mt-1 w-full rounded border px-2 py-1 text-sm"
                  value={form.registration_date}
                  onChange={(e) => setForm((f) => ({ ...f, registration_date: e.target.value }))}
                />
              </label>
              <label className="text-xs text-gray-600 sm:col-span-2" htmlFor="maint-customer">Customer search via Bexio
                <div className="mt-1 flex items-center gap-2">
                  <input
                    id="maint-customer"
                    data-testid="maint-customer"
                    className="w-full rounded border px-2 py-1 text-sm"
                    value={form.customer_name}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      customer_name: e.target.value,
                      bexio_contact_id: null,
                      bexio_contact_nr: null,
                    }))}
                    placeholder="Type customer name or pick from Bexio..."
                  />
                  <BexioContactPicker
                    onPick={(contact) => {
                      setForm((f) => applyBexioContactToForm(f, contact));
                    }}
                  />
                </div>
                {formatLinkedBexioContact(form) ? (
                  <div className="mt-1 text-[11px] text-emerald-700" data-testid="maint-bexio-link">
                    {formatLinkedBexioContact(form)}
                  </div>
                ) : null}
              </label>
              <label className="text-xs text-gray-600">Billing frequency
                <select className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.billing_frequency} onChange={(e) => setForm((f) => ({ ...f, billing_frequency: e.target.value }))}>
                  {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-600">Maintenance fee amount<input type="number" step="0.01" className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.maintenance_fee_amount} onChange={(e) => setForm((f) => ({ ...f, maintenance_fee_amount: e.target.value }))} /></label>
              <label className="text-xs text-gray-600">Currency
                <select className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}>
                  {CURRENCIES.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-600">Invoice status
                <select className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.invoice_status} onChange={(e) => setForm((f) => ({ ...f, invoice_status: e.target.value }))}>
                  {INVOICE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-600">Bexio invoice/reference<input className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.bexio_invoice_id} onChange={(e) => setForm((f) => ({ ...f, bexio_invoice_id: e.target.value }))} /></label>
              <label className="text-xs text-gray-600">Hosting provider<input className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.hosting_provider} onChange={(e) => setForm((f) => ({ ...f, hosting_provider: e.target.value }))} /></label>
              <label className="text-xs text-gray-600">Internal owner<input className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.internal_owner} onChange={(e) => setForm((f) => ({ ...f, internal_owner: e.target.value }))} /></label>
              <label className="text-xs text-gray-600 sm:col-span-2">
                Reminder recipient email(s)
                <input
                  type="text"
                  className="mt-1 w-full rounded border px-2 py-1 text-sm"
                  placeholder="ops@evo-home.ch, finance@client.com"
                  value={form.responsible_email}
                  onChange={(e) => setForm((f) => ({ ...f, responsible_email: e.target.value }))}
                />
                <span className="mt-1 block text-[11px] text-gray-500">
                  Required for Launch and for all prep/reminder emails on this line (comma-separated). There is no global production fallback.
                </span>
              </label>
              <label className="text-xs text-gray-600">Drip language
                <select className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.drip_language || 'en'} onChange={(e) => setForm((f) => ({ ...f, drip_language: e.target.value }))}>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </label>
              <label className="text-xs text-gray-600">Last invoice date<input type="date" className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.last_invoice_date} onChange={(e) => setForm((f) => ({ ...f, last_invoice_date: e.target.value }))} /></label>
              <label className="text-xs text-gray-600">Last paid date<input type="date" className="mt-1 w-full rounded border px-2 py-1 text-sm" value={form.last_paid_date} onChange={(e) => setForm((f) => ({ ...f, last_paid_date: e.target.value }))} /></label>
              <label className="text-xs text-gray-600 sm:col-span-2">Notes<textarea className="mt-1 w-full rounded border px-2 py-1 text-sm" rows={4} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></label>
              <label className="inline-flex items-center gap-2 text-xs text-gray-700 sm:col-span-2">
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
                Active
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>Cancel</Button>
              <Button type="button" data-testid="maint-save" onClick={() => { void saveItem(); }}>
                {editingId ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={linkDialogOpen}
        onOpenChange={(open) => {
          setLinkDialogOpen(open);
          if (!open) {
            setLinkTargetItem(null);
            setLinkInvoices([]);
            setLinkFilter('');
            setLinkMessage('');
            setLinkLoading(false);
            setLinkingInvoiceId(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Link Bexio Invoice</DialogTitle>
            <DialogDescription>
              {linkTargetItem ? `Select a Bexio invoice to link with ${linkTargetItem.domain_name}.` : 'Select invoice'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              className="w-full rounded border px-2 py-1.5 text-sm"
              placeholder="Filter invoice number, title, or id"
              value={linkFilter}
              onChange={(e) => setLinkFilter(e.target.value)}
            />
            {linkLoading ? (
              <div className="rounded border px-3 py-6 text-sm text-gray-500">Loading Bexio invoices...</div>
            ) : linkMessage ? (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{linkMessage}</div>
            ) : (
              <div className="max-h-[55vh] overflow-auto rounded border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="px-2 py-2 text-left">ID</th>
                      <th className="px-2 py-2 text-left">Document</th>
                      <th className="px-2 py-2 text-left">Title</th>
                      <th className="px-2 py-2 text-right">Amount</th>
                      <th className="px-2 py-2 text-left">Status</th>
                      <th className="px-2 py-2 text-right">Score</th>
                      <th className="px-2 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkInvoices
                      .filter((inv) => {
                        const q = linkFilter.trim().toLowerCase();
                        if (!q) return true;
                        const hay = `${inv.id} ${inv.document_nr || ''} ${inv.title || ''}`.toLowerCase();
                        return hay.includes(q);
                      })
                      .map((inv) => (
                        <tr key={inv.id} className="border-t">
                          <td className="px-2 py-2 tabular-nums">{inv.id}</td>
                          <td className="px-2 py-2">{inv.document_nr || '-'}</td>
                          <td className="px-2 py-2">{inv.title || '-'}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{inv.total_net || inv.total || '0'} {inv.currency_code || 'CHF'}</td>
                          <td className="px-2 py-2">{inv.status_label || '-'}</td>
                          <td className="px-2 py-2 text-right tabular-nums">
                            {inv.match_score || 0}
                            {Array.isArray(inv.match_reasons) && inv.match_reasons.length > 0 ? (
                              <span className="ml-1 text-[10px] text-gray-500">({inv.match_reasons.join(', ')})</span>
                            ) : null}
                          </td>
                          <td className="px-2 py-2 text-right">
                            <Button
                              size="sm"
                              onClick={() => submitLinkBexioInvoice(inv.id)}
                              disabled={linkingInvoiceId === inv.id}
                            >
                              {linkingInvoiceId === inv.id ? 'Linking...' : 'Link'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    {linkInvoices.length === 0 && (
                      <tr><td className="px-2 py-6 text-center text-gray-400" colSpan={7}>No invoices found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
